import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiBriefcaseLight, PiCalendarCheckLight, PiCheckCircleLight, PiColumnsLight, PiEyeLight, PiFileTextLight, PiCircleNotchLight, PiLockKeyLight, PiEnvelopeSimpleLight, PiMapPinLight, PiPhoneLight, PiMagnifyingGlassLight, PiShieldCheckLight, PiSlidersHorizontalLight, PiThumbsDownLight, PiUserLight, PiUsersLight, PiXLight } from 'react-icons/pi';
import { candidateAPI, jobAPI } from '../../services/api';
import { formatCompensationRange } from '../../utils/compensation';

const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatMoney = (salary) => formatCompensationRange(salary);

const scoreTone = (score) => {
  const numeric = Number(score) || 0;
  if (numeric >= 80) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (numeric >= 65) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (numeric >= 50) return 'border-amber-100 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['mutual', 'interview_passed', 'assessment_passed', 'offer_accepted', 'hired'].includes(normalized)) {
    return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  }
  if (['employer_interested', 'candidate_interested', 'interview_scheduled', 'assessment_completed'].includes(normalized)) {
    return 'border-sky-100 bg-sky-50 text-sky-700';
  }
  if (['interview_needs_discussion', 'hold', 'potential'].includes(normalized)) {
    return 'border-amber-100 bg-amber-50 text-amber-700';
  }
  if (['employer_rejected', 'interview_failed', 'candidate_declined', 'assessment_failed'].includes(normalized)) {
    return 'border-rose-100 bg-rose-50 text-rose-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const getSkillName = (skill) => (
  typeof skill === 'string'
    ? skill
    : skill?.name || skill?.skill_name || skill?.skillName || 'Skill'
);

const contactHiddenLabel = (contact) => (
  contact?.policy === 'candidate_privacy_hidden_contact'
    ? 'Candidate has chosen not to share contact details.'
    : 'Contact details unlock after mutual interest.'
);

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => {
  const tones = {
    sky: 'text-sky-600',
    slate: 'text-slate-800',
    green: 'text-emerald-600',
    amber: 'text-amber-600'
  };

  return (
    <div className="min-w-0 rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className={`flex shrink-0 items-center justify-center ${tones[tone] || tones.sky}`}>
          <Icon size={24} />
        </span>
        <span className="truncate text-xs font-medium text-slate-400">{detail}</span>
      </div>
      <div className="truncate font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
      <div className="mt-2 truncate text-sm font-medium text-slate-700">{label}</div>
    </div>
  );
};

const LocalSkeletonBlock = ({ className = '', style }) => (
  <div 
    className={`relative overflow-hidden bg-slate-200/60 ${className}`} 
    style={style}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

const CandidatesOverviewSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }
    `}</style>
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <LocalSkeletonBlock className="h-3 w-24 rounded-md" />
          <LocalSkeletonBlock className="h-9 w-64 rounded-md" />
          <LocalSkeletonBlock className="h-4 w-[34rem] max-w-full rounded-md" />
        </div>
        <LocalSkeletonBlock className="h-10 w-36 rounded-lg" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <LocalSkeletonBlock className="h-9 w-9 rounded-lg" />
              <LocalSkeletonBlock className="h-3 w-20 rounded-md" />
            </div>
            <LocalSkeletonBlock className="h-8 w-20 rounded-md" />
            <LocalSkeletonBlock className="mt-2 h-4 w-24 rounded-md" />
          </div>
        ))}
      </div>

      <section className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 lg:flex-row">
            <LocalSkeletonBlock className="h-10 flex-1 rounded-lg lg:max-w-sm" />
            <LocalSkeletonBlock className="h-10 w-full rounded-lg lg:w-44" />
            <LocalSkeletonBlock className="h-10 w-full rounded-lg lg:w-40" />
          </div>
          <div className="flex items-center gap-2">
            <LocalSkeletonBlock className="h-10 w-24 rounded-lg" />
            <LocalSkeletonBlock className="h-10 w-52 rounded-lg" />
          </div>
        </div>

        <div className="px-4 py-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="grid min-w-[1120px] grid-cols-[2fr_1.5fr_0.9fr_1fr_1.4fr_1.2fr_1fr] gap-4 border-b border-slate-100 py-4 last:border-0">
              <div className="space-y-2">
                <LocalSkeletonBlock className="h-4 w-40 rounded-md" />
                <LocalSkeletonBlock className="h-3 w-52 rounded-md" />
              </div>
              <LocalSkeletonBlock className="h-4 w-32 rounded-md" />
              <LocalSkeletonBlock className="h-7 w-16 rounded-md" />
              <LocalSkeletonBlock className="h-7 w-24 rounded-md" />
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 3 }).map((__, chipIndex) => <LocalSkeletonBlock key={chipIndex} className="h-6 w-16 rounded-md" />)}
              </div>
              <LocalSkeletonBlock className="h-4 w-24 rounded-md" />
              <div className="flex justify-end gap-1.5">
                {Array.from({ length: 4 }).map((__, buttonIndex) => <LocalSkeletonBlock key={buttonIndex} className="h-8 w-8 rounded-md" />)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  </div>
);

const EmptyState = ({ title, copy, icon: Icon = PiUserLight, actionButton }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="mb-4 flex items-center justify-center text-sky-600">
      <Icon size={26} />
    </div>
    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{title}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{copy}</p>
    {actionButton && <div className="mt-5">{actionButton}</div>}
  </div>
);

const MatchBadges = ({ match }) => {
  const score = Math.round(Number(match.overallMatchScore || 0));
  return (
    <div className="flex flex-wrap gap-2">
      <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${scoreTone(score)}`}>
        Band {match.matchBand || '-'} · {score}%
      </span>
      <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${statusTone(match.matchStatus)}`}>
        {labelize(match.matchStatus)}
      </span>
      {match.employerAction && (
        <span className="rounded-md border border-sky-100 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
          Employer {labelize(match.employerAction)}
        </span>
      )}
      {match.candidateAction && (
        <span className="rounded-md border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
          Candidate {labelize(match.candidateAction)}
        </span>
      )}
    </div>
  );
};

const CandidateIdentity = ({ match, compact = false }) => {
  const candidate = match.candidate || {};
  const name = candidate.fullName || 'Candidate';
  const salary = formatMoney(candidate.salaryExpectations);

  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sm font-medium text-sky-700">
        {initialsFor(name)}
      </div>
      <div className="min-w-0">
        <div className="truncate font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{name}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
          {candidate.experienceLevel && <span>{labelize(candidate.experienceLevel)}</span>}
          {candidate.experienceYears !== null && candidate.experienceYears !== undefined && <span>{candidate.experienceYears} yrs</span>}
          {candidate.location && <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {candidate.location}</span>}
          {candidate.availability && <span>{labelize(candidate.availability)}</span>}
          {!compact && salary && <span>{salary}</span>}
        </div>
      </div>
    </div>
  );
};

const CandidateCard = ({ match, actionBusy, onAction, onSchedule, onOpen }) => {
  const candidate = match.candidate || {};
  const skills = candidate.skills || [];
  const contact = candidate.contact || {};
  const workflow = match.workflow || {};
  const canSchedule = match.employerAction === 'interested' && match.candidateAction === 'interested';
  const isPotential = match.matchStatus === 'potential';
  const isMutual = match.matchStatus === 'mutual';

  return (
    <article className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <CandidateIdentity match={match} />
            <MatchBadges match={match} />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Role context</div>
              <div className="mt-1 truncate text-sm font-medium text-[#0b1f3a]">{match.job?.title || 'Untitled role'}</div>
              <div className="mt-1 text-xs text-slate-500">{match.job?.location || 'No location'} · {labelize(match.job?.jobType)}</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Workflow</div>
              <div className="mt-1 text-sm font-medium text-[#0b1f3a]">
                {formatNumber(workflow.interviews?.total)} interviews · {formatNumber(workflow.assessments?.total)} assessments
              </div>
              <div className="mt-1 text-xs text-slate-500">{formatNumber(workflow.offers?.total)} offers · {workflow.offers?.latestStatus ? labelize(workflow.offers.latestStatus) : 'No active offer'}</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">Evidence</div>
              <div className="mt-1 text-sm font-medium text-[#0b1f3a]">{candidate.cv?.filename || 'No CV file shown'}</div>
              <div className="mt-1 text-xs text-slate-500">{candidate.profileCompletionPercentage ?? 0}% onboarding complete</div>
            </div>
          </div>

          {skills.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {skills.slice(0, 12).map((skill, index) => (
                <span key={`${getSkillName(skill)}-${index}`} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600">
                  {getSkillName(skill)}
                </span>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
            {contact.visible ? (
              <>
                {contact.email && <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1"><PiEnvelopeSimpleLight size={12} /> {contact.email}</span>}
                {contact.phone && <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1"><PiPhoneLight size={12} /> {contact.phone}</span>}
              </>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1 text-slate-500">
                <PiLockKeyLight size={12} /> {contactHiddenLabel(contact)}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 xl:w-44 xl:flex-col">
          <button
            onClick={() => onOpen(match)}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <PiEyeLight size={12} /> Profile
          </button>
          <button
            onClick={() => onAction(match, 'interested')}
            disabled={Boolean(actionBusy) || match.employerAction === 'interested'}
            className={
              isPotential
                ? "inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 text-xs font-medium text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60 btn-premium-base btn-premium-highlight-sky"
                : "inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-3 text-xs font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-60"
            }
          >
            {actionBusy === 'interested' ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiCheckCircleLight size={12} />}
            Interest
          </button>
          <button
            onClick={() => onSchedule(match)}
            disabled={!canSchedule}
            className={
              isMutual
                ? "inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 text-xs font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45 btn-premium-base btn-premium-highlight-emerald"
                : "inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-45"
            }
          >
            <PiCalendarCheckLight size={12} /> Interview
          </button>
          <button
            onClick={() => onAction(match, 'not_interested')}
            disabled={Boolean(actionBusy) || ['rejected', 'not_interested'].includes(match.employerAction)}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60"
          >
            {actionBusy === 'not_interested' ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiThumbsDownLight size={12} />}
            Pass
          </button>
        </div>
      </div>
    </article>
  );
};

const CandidateRow = ({ match, actionBusy, onAction, onSchedule, onOpen }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const candidate = match.candidate || {};
  const workflow = match.workflow || {};
  const contact = candidate.contact || {};
  const score = Math.round(Number(match.overallMatchScore || 0));
  const canSchedule = match.employerAction === 'interested' && match.candidateAction === 'interested';
  const isPotential = match.matchStatus === 'potential';
  const isMutual = match.matchStatus === 'mutual';

  return (
    <>
      <tr className={`group transition-colors hover:bg-slate-50/60 last:border-0 ${isExpanded ? '' : 'border-b border-slate-100'}`}>
        <td className="py-4 pl-4 pr-4 align-top">
          <div className="flex items-start gap-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 shrink-0 rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-all"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
            <button onClick={() => onOpen(match)} className="block max-w-[320px] text-left">
              <CandidateIdentity match={match} compact />
            </button>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="max-w-[260px] pt-1">
            <div className="truncate text-sm font-medium text-[#0b1f3a]">{match.job?.title || 'Untitled role'}</div>
            <div className="mt-1 text-xs text-slate-500">{match.job?.location || 'No location'}</div>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="pt-1">
            <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-medium ${scoreTone(score)}`}>
              Band {match.matchBand || '-'} · {score}%
            </span>
          </div>
        </td>
        <td className="py-4 pr-4 align-top">
          <div className="pt-1">
            <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-medium ${statusTone(match.matchStatus)}`}>
              {labelize(match.matchStatus)}
            </span>
          </div>
        </td>
        <td className="py-4 pr-4 align-top text-sm text-slate-600">
          <div className="pt-1 max-w-[180px]">
            {candidate.skills?.slice(0, 3).map(getSkillName).join(', ') || 'No skills'}
          </div>
        </td>
        <td className="py-4 pr-4 align-top text-sm text-slate-600">
          <div className="pt-1">
            {formatNumber(workflow.interviews?.total)} / {formatNumber(workflow.assessments?.total)} / {formatNumber(workflow.offers?.total)}
          </div>
        </td>
        <td className="py-4 pr-4 align-top text-right">
          <div className="flex justify-end gap-1.5 pt-0.5">
            <button onClick={() => onOpen(match)} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700" title="Open profile">
              <PiEyeLight size={12} />
            </button>
            <button 
              onClick={() => onAction(match, 'interested')} 
              disabled={Boolean(actionBusy) || match.employerAction === 'interested'} 
              className={
                isPotential
                  ? "flex h-8 w-8 items-center justify-center rounded-md bg-sky-600 text-white transition hover:bg-sky-700 disabled:opacity-50 btn-premium-base btn-premium-highlight-sky"
                  : "flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
              }
              title="Express interest"
            >
              {actionBusy === 'interested' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiCheckCircleLight size={12} />}
            </button>
            <button 
              onClick={() => onSchedule(match)} 
              disabled={!canSchedule} 
              className={
                isMutual
                  ? "flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white transition hover:bg-emerald-700 disabled:opacity-40 btn-premium-base btn-premium-highlight-emerald"
                  : "flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:opacity-40"
              }
              title="Schedule interview"
            >
              <PiCalendarCheckLight size={12} />
            </button>
            <button onClick={() => onAction(match, 'not_interested')} disabled={Boolean(actionBusy) || ['rejected', 'not_interested'].includes(match.employerAction)} className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50" title="Pass">
              {actionBusy === 'not_interested' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiThumbsDownLight size={12} />}
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-slate-100 bg-white/50">
          <td colSpan={7} className="pb-5 pl-14 pr-4 pt-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">AI Dossier & Experience</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#0b1f3a]">
                    {candidate.summary || candidate.professionalSummary || 'No summary provided by candidate.'}
                  </p>
                  {candidate.latestExperience && (
                    <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 border border-slate-100">
                      <p className="text-xs font-medium text-[#0b1f3a]">{candidate.latestExperience.title || 'Latest Role'}</p>
                      <p className="text-xs text-slate-500">{candidate.latestExperience.company || 'Unknown Company'} • {candidate.latestExperience.duration || 'Duration unknown'}</p>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Compensation Expectation</p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-medium text-[#0b1f3a]">
                      {formatMoney(candidate.salaryExpectations) || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Contact Control</p>
                    <div className="mt-1 flex flex-col gap-1.5 text-sm font-medium text-[#0b1f3a]">
                      {contact.visible ? (
                        <>
                          {contact.email && <span className="inline-flex items-center gap-1.5"><PiEnvelopeSimpleLight size={12} className="text-slate-400" /> {contact.email}</span>}
                          {contact.phone && <span className="inline-flex items-center gap-1.5"><PiPhoneLight size={12} className="text-slate-400" /> {contact.phone}</span>}
                        </>
                      ) : (
                        <span className="inline-flex items-start gap-1.5 text-slate-500">
                          <PiLockKeyLight size={12} className="mt-0.5 shrink-0" /> <span className="text-xs">{contactHiddenLabel(contact)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const pipelineColumns = [
  { id: 'potential', label: 'Potential' },
  { id: 'employer_interested', label: 'Employer interest' },
  { id: 'mutual', label: 'Mutual' },
  { id: 'interview_passed', label: 'Interview passed' },
  { id: 'assessment_completed', label: 'Assessment done' },
  { id: 'employer_rejected', label: 'Closed' }
];

const CandidatesOverview = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    matchStatus: '',
    employerAction: '',
    candidateAction: '',
    matchBand: '',
    minScore: 30,
    sortBy: 'match_score',
    sortOrder: 'DESC',
    limit: 50,
    offset: 0
  });
  const [isHighlighting, setIsHighlighting] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsHighlighting(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.matchStatus ||
    filters.employerAction ||
    filters.candidateAction ||
    filters.matchBand ||
    Number(filters.minScore) !== 30
  );

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) acc[key] = value;
        return acc;
      }, {});
      const response = await candidateAPI.getEmployerCandidates(params);
      setCandidates(Array.isArray(response.candidates) ? response.candidates : []);
      setSummary(response.summary || null);
      setPagination(response.pagination || null);
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load candidate pipeline'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const metrics = useMemo(() => ({
    totalCandidates: summary?.totalCandidates || 0,
    totalMatches: summary?.totalMatches || pagination?.total || 0,
    mutualMatches: summary?.mutualMatches || 0,
    averageScore: summary?.averageScore || 0,
    pendingCandidateResponse: summary?.pendingCandidateResponse || 0
  }), [pagination, summary]);

  const pipeline = useMemo(() => {
    const grouped = pipelineColumns.reduce((acc, column) => ({ ...acc, [column.id]: [] }), {});
    candidates.forEach((match) => {
      const status = match.matchStatus || 'potential';
      const key = grouped[status] ? status : (String(status).includes('reject') || String(status).includes('failed') ? 'employer_rejected' : 'potential');
      grouped[key].push(match);
    });
    return grouped;
  }, [candidates]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value, offset: 0 }));
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      matchStatus: '',
      employerAction: '',
      candidateAction: '',
      matchBand: '',
      minScore: 30,
      sortBy: 'match_score',
      sortOrder: 'DESC',
      limit: 50,
      offset: 0
    });
  };

  const handleAction = async (match, action) => {
    setActionKey(`${match.matchId}:${action}`);
    try {
      await jobAPI.takeMatchAction(match.jobId, match.matchId, {
        action,
        notifyCandidate: action === 'interested'
      });
      toast.success(action === 'interested' ? 'Interest recorded and candidate notified.' : 'Candidate marked as passed.');
      loadCandidates();
    } catch (actionError) {
      toast.error(extractError(actionError, 'Could not update match'));
    } finally {
      setActionKey('');
    }
  };

  const handleOpen = (match) => {
    navigate(`/candidates/${match.candidateId}?jobId=${match.jobId}&matchId=${match.matchId}`);
  };

  const handleSchedule = (match) => {
    navigate(`/interviews/schedule?jobId=${match.jobId}&matchId=${match.matchId}&candidateId=${match.candidateId}`);
  };

  const handleReviewCandidates = () => {
    updateFilter('matchStatus', 'potential');
    setTimeout(() => {
      const section = document.getElementById('candidate-list-section');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  if (loading && candidates.length === 0) {
    return <CandidatesOverviewSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
      <style>{`
        @keyframes shimmer-sweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes subtle-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
          50% { box-shadow: 0 0 0 3px rgba(0, 0, 0, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
        }
        @keyframes subtle-pulse-ring-emerald {
          0% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(5, 150, 105, 0); }
          100% { box-shadow: 0 0 0 0 rgba(5, 150, 105, 0); }
        }
        @keyframes subtle-pulse-ring-sky {
          0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(2, 132, 199, 0); }
          100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
        }
        .btn-premium-base {
          position: relative;
          overflow: hidden;
        }
        .btn-premium-base::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%);
          background-size: 200% 100%;
          animation: shimmer-sweep 2s infinite linear;
          opacity: 0;
          transition: opacity 1s ease-in-out;
          pointer-events: none;
        }
        .btn-premium-highlight::after {
          opacity: 1;
        }
        .btn-premium-highlight {
          animation: subtle-pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-emerald {
          animation: subtle-pulse-ring-emerald 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        .btn-premium-highlight-sky {
          animation: subtle-pulse-ring-sky 2s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Candidates</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Candidate Pipeline</h1>
            {/* <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Review matches, candidate evidence, mutual-interest contact controls, and workflow state across jobs.
            </p> */}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/candidates/bands')}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-[#0b1f3a]"
            >
              <PiShieldCheckLight size={13} /> Matching bands
            </button>
            {metrics.totalCandidates > 0 && (
              <button
                onClick={handleReviewCandidates}
                className={`inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                  isHighlighting
                    ? 'bg-black btn-premium-highlight'
                    : 'bg-[#0b1f3a] hover:bg-[#12345c]'
                }`}
              >
                <PiUsersLight size={13} /> Review candidates
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Metric icon={PiUsersLight} label="Candidates" value={formatNumber(metrics.totalCandidates)} detail="Distinct profiles" tone="slate" />
          <Metric icon={PiFileTextLight} label="Matches" value={formatNumber(metrics.totalMatches)} detail="Role match records" tone="sky" />
          <Metric icon={PiShieldCheckLight} label="Average score" value={`${metrics.averageScore}%`} detail="Filtered view" tone="green" />
          <Metric icon={PiCheckCircleLight} label="Mutual interest" value={formatNumber(metrics.mutualMatches)} detail="Ready for next step" tone="amber" />
          <Metric icon={PiEnvelopeSimpleLight} label="Awaiting response" value={formatNumber(metrics.pendingCandidateResponse)} detail="Candidate action" tone="slate" />
        </div>

        <section id="candidate-list-section" className="rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-col gap-3 lg:flex-row">
              <div className="relative min-w-0 flex-1 lg:max-w-sm">
                <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input
                  value={filters.search}
                  onChange={(event) => updateFilter('search', event.target.value)}
                  className={`${inputClass} w-full pl-9`}
                  placeholder="Search candidates, roles, skills"
                />
              </div>
              <select value={filters.matchStatus} onChange={(event) => updateFilter('matchStatus', event.target.value)} className={inputClass}>
                <option value="">All statuses</option>
                <option value="potential">Potential</option>
                <option value="employer_interested">Employer interested</option>
                <option value="candidate_interested">Candidate interested</option>
                <option value="mutual">Mutual</option>
                <option value="interview_passed">Interview passed</option>
                <option value="assessment_completed">Assessment completed</option>
                <option value="employer_rejected">Employer rejected</option>
              </select>
              <select value={filters.sortBy} onChange={(event) => updateFilter('sortBy', event.target.value)} className={inputClass}>
                <option value="match_score">Match score</option>
                <option value="updated_at">Last updated</option>
                <option value="created_at">Newest match</option>
                <option value="candidate">Candidate name</option>
                <option value="job">Job title</option>
                <option value="experience">Experience</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters((value) => !value)}
                className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-medium transition ${showFilters ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <PiSlidersHorizontalLight size={13} /> Filters
              </button>
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                <button
                  onClick={() => setViewMode('table')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium ${viewMode === 'table' ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  <PiFileTextLight size={12} /> Table
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium ${viewMode === 'cards' ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  <PiUsersLight size={12} /> Cards
                </button>
                <button
                  onClick={() => setViewMode('pipeline')}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium ${viewMode === 'pipeline' ? 'bg-white text-[#0b1f3a] shadow-sm' : 'text-slate-500'}`}
                >
                  <PiColumnsLight size={12} /> Flow
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="grid gap-3 border-b border-slate-100 p-4 md:grid-cols-5">
              <select value={filters.matchBand} onChange={(event) => updateFilter('matchBand', event.target.value)} className={inputClass}>
                <option value="">All bands</option>
                <option value="A">Band A</option>
                <option value="B">Band B</option>
                <option value="C">Band C</option>
                <option value="D">Band D</option>
              </select>
              <select value={filters.employerAction} onChange={(event) => updateFilter('employerAction', event.target.value)} className={inputClass}>
                <option value="">Employer action</option>
                <option value="none">No action</option>
                <option value="interested">Interested</option>
                <option value="contacted">Contacted</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={filters.candidateAction} onChange={(event) => updateFilter('candidateAction', event.target.value)} className={inputClass}>
                <option value="">Candidate action</option>
                <option value="none">No action</option>
                <option value="interested">Interested</option>
                <option value="rejected">Rejected</option>
              </select>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minScore}
                onChange={(event) => updateFilter('minScore', event.target.value)}
                className={inputClass}
                placeholder="Min score"
              />
              <button onClick={resetFilters} className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
                Reset
              </button>
            </div>
          )}

          {candidates.length === 0 ? (
            <div className="p-4">
              {hasActiveFilters ? (
                <EmptyState
                  title="No matches for these filters"
                  copy="We couldn't find any candidates matching your current filter criteria."
                  actionButton={
                    <button
                      onClick={resetFilters}
                      className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-[#0b1f3a]"
                    >
                      <PiXLight size={13} /> Clear all filters
                    </button>
                  }
                />
              ) : (
                <EmptyState
                  title="No candidates found"
                  copy="Publish jobs and run matching to start discovering top candidates instantly."
                  actionButton={
                    <button
                      onClick={() => navigate('/jobs/create')}
                      className={`inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium text-white btn-premium-base transition-colors duration-1000 ${
                        isHighlighting
                          ? 'bg-black btn-premium-highlight'
                          : 'bg-[#0b1f3a] hover:bg-[#12345c]'
                      }`}
                    >
                      <PiBriefcaseLight size={13} /> Create a Job
                    </button>
                  }
                />
              )}
            </div>
          ) : viewMode === 'table' ? (
            <div className="overflow-x-auto px-4">
              <table className="w-full min-w-[1120px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                    <th className="py-3 pl-4 pr-4">Candidate</th>
                    <th className="py-3 pr-4">Role</th>
                    <th className="py-3 pr-4">Score</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Skills</th>
                    <th className="py-3 pr-4">Workflow</th>
                    <th className="py-3 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((match) => (
                    <CandidateRow
                      key={match.matchId}
                      match={match}
                      actionBusy={actionKey.startsWith(`${match.matchId}:`) ? actionKey.split(':')[1] : ''}
                      onAction={handleAction}
                      onSchedule={handleSchedule}
                      onOpen={handleOpen}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : viewMode === 'pipeline' ? (
            <div className="flex gap-4 overflow-x-auto p-4">
              {pipelineColumns.map((column) => (
                <div key={column.id} className="min-w-[280px] flex-1">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-medium text-[#0b1f3a]">{column.label}</h2>
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500">{pipeline[column.id]?.length || 0}</span>
                  </div>
                  <div className="space-y-3">
                    {(pipeline[column.id] || []).map((match) => {
                      const isPotential = match.matchStatus === 'potential';
                      const matchActionBusy = actionKey.startsWith(`${match.matchId}:`) ? actionKey.split(':')[1] : '';
                      return (
                        <div
                          key={match.matchId}
                          className="group relative w-full rounded-lg border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.035)] transition-all hover:border-sky-200 hover:shadow-md"
                        >
                          <button
                            onClick={() => handleOpen(match)}
                            className="w-full p-3 text-left block"
                          >
                            <CandidateIdentity match={match} compact />
                            <div className="mt-3 flex items-center justify-between text-xs">
                              <span className={`rounded-md border px-2 py-1 font-medium ${scoreTone(match.overallMatchScore)}`}>
                                {Math.round(Number(match.overallMatchScore || 0))}%
                              </span>
                              <span className="truncate pl-2 text-slate-500">{match.job?.title}</span>
                            </div>
                          </button>
                          
                          {isPotential && (
                            <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAction(match, 'interested'); }}
                                disabled={Boolean(matchActionBusy) || match.employerAction === 'interested'}
                                className="flex h-8 w-8 items-center justify-center rounded-md bg-white/95 backdrop-blur-sm border border-slate-200 text-slate-500 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
                                title="Express interest"
                              >
                                {matchActionBusy === 'interested' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiCheckCircleLight size={12} />}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAction(match, 'not_interested'); }}
                                disabled={Boolean(matchActionBusy) || ['rejected', 'not_interested'].includes(match.employerAction)}
                                className="flex h-8 w-8 items-center justify-center rounded-md bg-white/95 backdrop-blur-sm border border-slate-200 text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
                                title="Pass"
                              >
                                {matchActionBusy === 'not_interested' ? <PiCircleNotchLight size={12} className="animate-spin" /> : <PiThumbsDownLight size={12} />}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {(!pipeline[column.id] || pipeline[column.id].length === 0) && (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">
                        Empty
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4 p-4">
              {candidates.map((match) => (
                <CandidateCard
                  key={match.matchId}
                  match={match}
                  actionBusy={actionKey.startsWith(`${match.matchId}:`) ? actionKey.split(':')[1] : ''}
                  onAction={handleAction}
                  onSchedule={handleSchedule}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          )}

          {pagination?.hasMore && (
            <div className="border-t border-slate-100 p-4 text-center text-xs text-slate-500">
              Showing {formatNumber(candidates.length)} of {formatNumber(pagination.total)} matches. Narrow filters or increase backend limit for a smaller review set.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CandidatesOverview;
